import { ApiTabIndex } from '@/Enums/ReportingDashboard/ApiTabIndex.enum';
import type GamReportType from '@/Enums/ReportingDashboard/Gam/ReportType.enum';
import type { IParsedAdvertiser } from '@/Interfaces/Advertiser.interface';
import type { IAbortSignalThunkArg } from '@/Interfaces/Slices/AsyncThunk.interface';
import { getSelectedAdvertiserCampaignFilters } from '@/helpers/filters/filterMapping';
import { getEffectiveFilters } from '@/helpers/reportingDashboard/filters';
import type { RootState } from '@/store';
import { AsyncThunkPayloadCreator } from '@reduxjs/toolkit';
import axios, { type AxiosError, HttpStatusCode } from 'axios';

export const GAM_REPORTING_URL = '/reportingDashboard/gam/report';
export const GAM_CAMPAIGNS_URL = '/reportingDashboard/gam/campaigns';

const GAM_MAX_RETRIES = 1;

export interface IGamReportFilters {
  gamOrganizationId: number;
  dateRangeStart: string;
  dateRangeEnd: string;
  whitelabelIds?: number[];
  /** Ribeye `advertisers.id` — resolved to GAM company ids on the API. */
  advertiserIds?: number[];
  /** GAM order ids — filters the nightly snapshots serve-time (#4235). */
  campaignIds?: number[];
}

export interface IGamReportPayload<TGamReportType = GamReportType> {
  reportType: TGamReportType;
  filters: IGamReportFilters;
}

// Mirrors postSimplifiReport: single-retry-on-404 absorbs the brief
// route-registration / Cloud Run cold-start window in preview envs.
async function postGamReport<TReport>({
  signal,
  body,
  maxRetries = GAM_MAX_RETRIES,
}: {
  signal?: AbortSignal;
  body: IGamReportPayload;
  maxRetries?: number;
}): Promise<TReport | undefined> {
  let response;
  let lastError: AxiosError | undefined;
  let attemptRetry = false;
  let retryCounter = 0;

  do {
    try {
      response = await axios.post(GAM_REPORTING_URL, body, { signal });
      attemptRetry = false;
      lastError = undefined;
    } catch (error) {
      if ((error as AxiosError)?.response?.status !== HttpStatusCode.NotFound) {
        throw error;
      }
      lastError = error as AxiosError;
      attemptRetry = true;
      retryCounter++;
    }
  } while (retryCounter <= maxRetries && attemptRetry);

  // Persistent 404 after retries means the route is genuinely gone (not just
  // cold-start). Surface to Sentry; the thunk's rejected case clears loading.
  if (lastError) throw lastError;

  return response?.data;
}

// TODO(#3615 follow-up): resolve from whitelabel.gam_organization_id once the
// FE model exposes it. WRAL seeded as id=1 by the ribeye-api migration.
const DEFAULT_GAM_ORGANIZATION_ID = 1;

export const buildGamReportFilters = (
  state: RootState,
  dateRangeStart: string,
  dateRangeEnd: string,
): IGamReportFilters => {
  const {
    me: { role },
    reportingDashboard: { filters: appliedFilters },
  } = state;
  // GAM snapshots are network-wide; scoping happens serve-time via
  // GamAdvertiserScopeService. Only read from appliedFilters (the explicit
  // filter bar state) — not getEffectiveFilters, which can inject inherited
  // whitelabels when switching tabs.
  const appliedFilter = getSelectedAdvertiserCampaignFilters<IParsedAdvertiser, 'ribeyeAdvertiserId'>(
    appliedFilters,
    role!,
    'ribeyeAdvertiserId',
  );

  // Pass whitelabelIds so the backend can scope to only GAM-mapped advertisers
  // within the selected whitelabels. Without this, selecting a whitelabel with
  // no GAM mappings falls through to the no-filter case and shows all network
  // data. Mirrors the Simplifi pattern.
  const whitelabelIds = (appliedFilters.whitelabels ?? []).map((wl: { value: number }) => wl.value);

  return {
    gamOrganizationId: DEFAULT_GAM_ORGANIZATION_ID,
    dateRangeStart,
    dateRangeEnd,
    ...(whitelabelIds.length ? { whitelabelIds } : {}),
    ...(appliedFilter.advertiserIds?.length ? { advertiserIds: appliedFilter.advertiserIds } : {}),
    ...(appliedFilter.campaignIds?.length ? { campaignIds: appliedFilter.campaignIds } : {}),
  };
};

/**
 * Campaign selector options. Served from the nightly Campaign snapshot, so the
 * list covers exactly the orders that delivered in the selected window.
 */
export async function fetchGamCampaigns(args: {
  signal?: AbortSignal;
  filters: IGamReportFilters;
}): Promise<{ dataIsRefreshing: boolean; campaigns: Array<{ id: number; name: string }> }> {
  const { data } = await axios.post(GAM_CAMPAIGNS_URL, { filters: args.filters }, { signal: args.signal });
  return { dataIsRefreshing: !!data?.dataIsRefreshing, campaigns: data?.campaigns ?? [] };
}

export const gamReportRequestHandler =
  <TReport extends object>(
    reportType: GamReportType,
    fallbackValues: TReport,
  ): AsyncThunkPayloadCreator<TReport, IAbortSignalThunkArg> =>
  async (payload, { getState, signal }) => {
    const state = getState() as RootState;
    const filters = getEffectiveFilters(state, ApiTabIndex.Gam);

    if (!filters.dateStart || !filters.dateEnd) {
      return fallbackValues;
    }

    const gamFilters = buildGamReportFilters(state, filters.dateStart, filters.dateEnd);

    const body: IGamReportPayload = {
      reportType,
      filters: gamFilters,
    };

    // Honor both signals: payload?.signal is the requestSaver cancel,
    // signal is redux-toolkit's automatic abort on dispatch.abort().
    const data = await postGamReport<TReport>({ signal: payload?.signal ?? signal, body });

    if (data && typeof data === 'object' && (data as { dataIsRefreshing?: boolean }).dataIsRefreshing) {
      return data;
    }

    return { ...(fallbackValues as object), ...((data as object) ?? {}) } as TReport;
  };

// Public wrapper for ad-hoc callers outside the Redux-thunk path.
export async function fetchGamReport<TReport = unknown>(args: {
  signal?: AbortSignal;
  body: IGamReportPayload;
  maxRetries?: number;
}): Promise<TReport | undefined> {
  return postGamReport<TReport>(args);
}
